export type WaitForElementOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  root?: Document;
  isVisible?: (element: HTMLElement) => boolean;
};

export type ElementWait = {
  promise: Promise<HTMLElement | null>;
  cancel(): void;
};

export function elementIsRendered(element: HTMLElement): boolean {
  if (!element.isConnected || element.closest('[hidden]')) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

/**
 * Resolves with the first rendered element matching `selector`, or null after `timeoutMs`.
 * Watches DOM changes (lazy pages, @if blocks, loading states) and also polls, since a CSS-only change
 * (an element becoming visible) fires no mutation on the element itself.
 */
export function waitForElement(selector: string, options: WaitForElementOptions = {}): ElementWait {
  const { timeoutMs = 6000, intervalMs = 150, root = document, isVisible = elementIsRendered } = options;
  let finish: (element: HTMLElement | null) => void = () => undefined;
  let done = false;
  let observer: MutationObserver | null = null;
  let poll: ReturnType<typeof setInterval> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = () => {
    done = true;
    observer?.disconnect();
    if (poll) clearInterval(poll);
    if (timer) clearTimeout(timer);
  };

  const check = () => {
    if (done) return;
    const element = Array.from(root.querySelectorAll<HTMLElement>(selector)).find(isVisible) ?? null;
    if (element) {
      cleanup();
      finish(element);
    }
  };

  const promise = new Promise<HTMLElement | null>(resolve => {
    finish = resolve;
    check();
    if (done) return;
    if (typeof MutationObserver !== 'undefined' && root.body) {
      observer = new MutationObserver(check);
      observer.observe(root.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'data-guide'] });
    }
    poll = setInterval(check, intervalMs);
    timer = setTimeout(() => {
      if (done) return;
      cleanup();
      resolve(null);
    }, timeoutMs);
  });

  return {
    promise,
    cancel: () => {
      if (done) return;
      cleanup();
      finish(null);
    },
  };
}
